import React, { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCfoScoreTrend } from '../../../hooks/analytics/useCfoReports.ts';
import { WidgetCard, formatDate } from '../controller/shared.tsx';
import { CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

/** Report #8 — Compliance Score Trend (12-month). Line chart with Healthy/Watch/Critical band zones. */
export const ScoreTrendChart: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoScoreTrend();
  const rows = data ?? [];

  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime())
        .map((r) => ({ date: r.snapshot_date, score: r.score, band: r.band })),
    [rows]
  );

  return (
    <WidgetCard
      title="Compliance Score Trend"
      description="Score history with Healthy (≥80) / Watch (≥60) / Critical bands"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load score trend.'}
      isEmpty={!isLoading && !isError && chartData.length === 0}
      emptyMessage="No score history yet — the nightly refresh hasn't run."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <ReferenceArea y1={80} y2={100} fill={SEMANTIC.good.chart} fillOpacity={0.06} />
            <ReferenceArea y1={60} y2={80} fill={SEMANTIC.warn.chart} fillOpacity={0.06} />
            <ReferenceArea y1={0} y2={60} fill={SEMANTIC.critical.chart} fillOpacity={0.06} />
            <XAxis
              dataKey="date"
              tickFormatter={(v: string) => formatDate(v)}
              tick={CHART_AXIS_TICK}
              axisLine={CHART_AXIS_LINE}
              tickLine={false}
            />
            <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
            <Tooltip
              labelFormatter={(v) => formatDate(v as string)}
              formatter={(value: number) => [`${value}`, 'Score']}
              contentStyle={CHART_TOOLTIP_STYLE}
              cursor={{ stroke: CHART_GRID_STROKE, strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke={SEMANTIC.info.chart}
              strokeWidth={2.5}
              dot={{ r: 3, strokeWidth: 0, fill: SEMANTIC.info.chart }}
              activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </WidgetCard>
  );
};

export default ScoreTrendChart;
